import { formatMoney } from '../utils/currency';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  UsersIcon,
  TruckIcon,
  MapIcon,
  CurrencyDollarIcon,
  ArrowUpIcon,
  ArrowDownIcon
} from '@heroicons/react/24/outline';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line, Doughnut } from 'react-chartjs-2';
import api from '../services/api';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const StatCard = ({ title, value, icon: Icon, trend, trendLabel, color }) => (
  <div className="card">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm text-gray-500">{title}</p>
        <p className="text-2xl font-bold text-gray-800 mt-1">{value}</p>
        {trend && trend.direction !== 'flat' && (
          <div className={`flex items-center mt-2 text-sm ${
            trend.direction === 'increase' ? 'text-green-600' : 'text-red-600'
          }`}>
            {trend.direction === 'increase' ? (
              <ArrowUpIcon className="w-4 h-4 mr-1" />
            ) : (
              <ArrowDownIcon className="w-4 h-4 mr-1" />
            )}
            {trend.delta}% {trendLabel}
          </div>
        )}
        {trend && trend.direction === 'flat' && (
          <div className="flex items-center mt-2 text-sm text-gray-400">
            — no change {trendLabel}
          </div>
        )}
      </div>
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color}`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
    </div>
  </div>
);

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, chartsRes] = await Promise.all([
        api.get('/admin/dashboard/stats'),
        api.get('/admin/dashboard/charts?period=7days')
      ]);

      setStats(statsRes.data.data);
      setChartData(chartsRes.data.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
      </div>
    );
  }

  const ridesChartData = {
    labels: chartData?.ridesPerDay?.map(d => d._id) || [],
    datasets: [
      {
        label: 'Rides',
        data: chartData?.ridesPerDay?.map(d => d.count) || [],
        fill: true,
        borderColor: 'rgb(99, 102, 241)',
        backgroundColor: 'rgba(99, 102, 241, 0.1)',
        tension: 0.4
      }
    ]
  };

  const rideTypeChartData = {
    labels: chartData?.ridesByType?.map(d => d._id) || [],
    datasets: [
      {
        data: chartData?.ridesByType?.map(d => d.count) || [],
        backgroundColor: [
          'rgba(99, 102, 241, 0.8)',
          'rgba(16, 185, 129, 0.8)',
          'rgba(245, 158, 11, 0.8)',
          'rgba(239, 68, 68, 0.8)'
        ]
      }
    ]
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800">Dashboard</h1>
        <p className="text-gray-500">Welcome back! Here's what's happening.</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Users"
          value={stats?.totalUsers || 0}
          icon={UsersIcon}
          trend={stats?.trends?.totalUsers}
          trendLabel="last 30 days"
          color="bg-indigo-500"
        />
        <StatCard
          title="Total Drivers"
          value={stats?.totalDrivers || 0}
          icon={TruckIcon}
          trend={stats?.trends?.totalDrivers}
          trendLabel="last 30 days"
          color="bg-green-500"
        />
        <StatCard
          title="Today's Rides"
          value={stats?.todayRides || 0}
          icon={MapIcon}
          trend={stats?.trends?.todayRides}
          trendLabel="vs yesterday"
          color="bg-yellow-500"
        />
        <StatCard
          title="Today's Revenue"
          value={formatMoney(stats?.todayRevenue || 0, stats?.currencyCode || 'QAR')}
          icon={CurrencyDollarIcon}
          trend={stats?.trends?.todayRevenue}
          trendLabel="vs yesterday"
          color="bg-purple-500"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Rides Chart */}
        <div className="card lg:col-span-2">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Rides Overview</h3>
          <div className="h-64">
            <Line
              data={ridesChartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: { display: false }
                },
                scales: {
                  y: { beginAtZero: true }
                }
              }}
            />
          </div>
        </div>

        {/* Ride Types Chart */}
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Ride Types</h3>
          <div className="h-64 flex items-center justify-center">
            <Doughnut
              data={rideTypeChartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: { position: 'bottom' }
                }
              }}
            />
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Quick Stats</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Active Drivers</span>
              <span className="font-semibold text-green-600">{stats?.activeDrivers || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Pending Approvals</span>
              <span className="font-semibold text-yellow-600">{stats?.pendingDrivers || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Month Revenue</span>
              <span className="font-semibold text-indigo-600">{formatMoney(stats?.monthRevenue || 0, stats?.currencyCode || 'QAR')}</span>
            </div>
          </div>
        </div>

        <div className="card">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">New This Month</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">New Users</span>
              <span className="font-semibold">{stats?.newUsersThisMonth || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">New Drivers</span>
              <span className="font-semibold">{stats?.newDriversThisMonth || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Total Rides</span>
              <span className="font-semibold">{stats?.monthRides || 0}</span>
            </div>
          </div>
        </div>

        <div className="card">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Quick Actions</h3>
          <div className="space-y-3">
            <Link to="/drivers?status=pending" className="btn-primary block text-center">
              Review Pending Drivers
            </Link>
            <Link to="/rides" className="btn-secondary block text-center">
              View All Rides
            </Link>
            <Link to="/users" className="btn-secondary block text-center">
              Manage Users
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
